import { Table } from '@radix-ui/themes'
import { flexRender, Row } from '@tanstack/react-table'
import React from 'react'
import { css, styled } from 'styled-components'

const StyledTableCell = styled(Table.Cell)`
  color: ${({ theme }) => theme.forecast.defaultText};
  padding: var(--table-cell-padding, 0.5rem);
  text-align: left;
  vertical-align: middle;
  min-height: 3.5rem;
  border-style: hidden !important;
  white-space: nowrap;
  max-width: 22rem;
  font-size: var(--table-cell-font-size, inherit);
  --table-row-box-shadow: none;
`

const StyledFirstCell = styled(Table.Cell)`
  display: flex;
  align-items: center;
`

const StyledTableRow = styled(Table.Row)<{
  $hasOnClick: boolean
  $depth?: number
  $enableExpanding?: boolean
  $isHighlighted?: boolean
  $isHovered?: boolean
  $cardStyleRows?: boolean
}>`
  position: relative;
  outline: none;
  scroll-margin-top: 3rem;

  ${({ $cardStyleRows, theme }) =>
    $cardStyleRows
      ? css`
          background-color: ${theme.forecast.cardBackground};
          td:first-child {
            border-radius: 0.5rem 0 0 0.5rem;
          }
          td:last-child {
            border-radius: 0 0.5rem 0.5rem 0;
          }
        `
      : css`
          background-color: transparent;
          &:nth-child(odd) {
            background-color: ${({ theme }) =>
              theme.forecast.cardGroupBackground};
          }
          td:first-child {
            padding-left: 0.5rem;
          }
          td:last-child {
            padding-right: 0.5rem;
          }
        `}

  &:focus {
    ${StyledTableCell} {
      background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
    }
  }

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      ${StyledTableCell} {
        background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
      }
    `}

  ${({ $isHovered }) =>
    $isHovered &&
    css`
      ${StyledTableCell} {
        background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
      }
    `}

  ${({ $hasOnClick }) =>
    $hasOnClick &&
    css`
      &:hover {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }

        &:hover * {
          cursor: pointer;
        }
      }

      &:active {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }
      }
    `}

  ${({ $depth = 0, $enableExpanding }) =>
    $depth > 0 &&
    css`
      ${StyledTableCell}:first-child {
        padding-left: ${$depth * 1.5}rem;
        position: relative;

        ${$enableExpanding &&
        css`
          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: ${$depth * 1.5}rem;
            background-color: ${({ theme }) => theme.forecast.pageBackground1};
            z-index: 0;
          }
        `}
      }
    `}
`

const ExpandButton = styled.button<{ $isPlaceholder?: boolean }>`
  background: none;
  border: none;
  margin-right: 0.25rem;
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  ${({ $isPlaceholder }) =>
    $isPlaceholder &&
    css`
      opacity: 0;
    `}
`

interface TableBodyRowProps<TData extends object> {
  row: Row<TData>
  enableExpanding?: boolean
  onRowClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowAuxClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowContextMenu?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowKeyDown?: (params: {
    event: React.KeyboardEvent<HTMLTableRowElement>
    row: Row<TData>
    rowElement: HTMLTableRowElement
  }) => void
  onRowFocus?: (row: Row<TData>) => void
  onRowHoverStart?: (row: Row<TData>) => void
  onRowHoverEnd?: () => void
  $isHighlighted?: boolean
  $isHovered?: boolean
  cardStyleRows?: boolean
}

export const TableBodyRow = <TData extends object>({
  row,
  enableExpanding,
  onRowClick,
  onRowAuxClick,
  onRowContextMenu,
  onRowKeyDown,
  onRowFocus,
  onRowHoverStart,
  onRowHoverEnd,
  $isHighlighted,
  $isHovered,
  cardStyleRows,
}: TableBodyRowProps<TData>) => {
  const canExpand = enableExpanding && row.getCanExpand()
  const isExpanded = enableExpanding && row.getIsExpanded()

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (enableExpanding && canExpand) {
      row.toggleExpanded()
    }

    if (onRowClick) {
      onRowClick(row, event)
    }
  }

  const handleAuxClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (onRowAuxClick) {
      onRowAuxClick(row, event)
    }
  }

  const handleContextMenu = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (onRowContextMenu) {
      onRowContextMenu(row, event)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (onRowKeyDown) {
      onRowKeyDown({ event, row, rowElement: event.currentTarget })
    }
  }

  const handleFocus = () => {
    if (onRowFocus) {
      onRowFocus(row)
    }
  }

  const handleMouseEnter = () => {
    if (onRowHoverStart) {
      onRowHoverStart(row)
    }
  }

  const handleMouseLeave = () => {
    if (onRowHoverEnd) {
      onRowHoverEnd()
    }
  }

  return (
    <StyledTableRow
      key={row.id}
      id={row.id}
      tabIndex={onRowKeyDown ? 0 : undefined}
      data-state={row.getIsSelected() && 'selected'}
      data-depth={row.depth}
      onClick={handleRowClick}
      onAuxClick={handleAuxClick}
      onContextMenu={handleContextMenu}
      onKeyDown={onRowKeyDown ? handleKeyDown : undefined}
      onFocus={onRowFocus ? handleFocus : undefined}
      onMouseEnter={onRowHoverStart ? handleMouseEnter : undefined}
      onMouseLeave={onRowHoverEnd ? handleMouseLeave : undefined}
      $hasOnClick={Boolean(onRowClick || (enableExpanding && canExpand))}
      $depth={row.depth}
      $enableExpanding={enableExpanding}
      $isHighlighted={$isHighlighted}
      $isHovered={$isHovered}
      $cardStyleRows={cardStyleRows}>
      {row.getVisibleCells().map((cell, index) => (
        <StyledTableCell key={cell.id}>
          {index === 0 && enableExpanding ? (
            <StyledFirstCell>
              <ExpandButton
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
                $isPlaceholder={!canExpand}
                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                {isExpanded ? '▼' : '▶'}
              </ExpandButton>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </StyledFirstCell>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  )
}
